import { notification } from 'antd';
import { NotificationPlacement } from 'antd/es/notification/interface';
import React, { ReactNode, createContext, useContext, useMemo } from 'react';
import styled from 'styled-components';

interface NotificationContextProps {
  openNotification: (options: NotificationOptions) => void;
}

interface NotificationProviderProps {
  children: ReactNode;
}

interface NotificationOptions {
  message: ReactNode;
  description?: string;
  type?: 'open' | 'success' | 'info' | 'warning' | 'error';
  duration?: number;
  placement?: NotificationPlacement;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined);

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [api, contextHolder] = notification.useNotification({ maxCount: 3 });

  const openNotification = ({
    message,
    description,
    type = 'open',
    duration = 3,
    placement = 'bottom',
  }: NotificationOptions) => {
    api[type]({
      message: <NotificationText>{message}</NotificationText>,
      description,
      duration,
      placement,
      closeIcon: null,
      style: {
        backgroundColor: '#505363',
        borderRadius: '8px',
        minHeight: '53px',
        padding: '16px',
      },
    });
  };

  const contextValue = useMemo(() => ({ openNotification }), []);

  return (
    <NotificationContext.Provider value={contextValue}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextProps => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

const NotificationText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;
